import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { initials } from "../../../lib/format";
import { t } from "../../../lib/sprache";

/**
 * K-524: kurzer Hinweis im Portal bei neuer Nachricht (Absender und Anfang des Texts, Klick öffnet den Faden). Ausgelöst vom
 * Zähler in unread.ts, nur wenn die Person den Faden nicht ohnehin vor sich hat (Chatseite dieses Creators oder Mini-Chat).
 * Ein Hinweis zur Zeit; ein neuer ersetzt den alten. Nach 8 Sekunden verschwindet er, solange die Maus nicht darauf liegt.
 */
export interface ChatHinweis {
  id: number;
  von: string;
  text: string;
  ziel: string;
  talentId: string;
}

let seq = 0;
let aktuell: ChatHinweis | null = null;
const hoerer = new Set<(h: ChatHinweis | null) => void>();
const melde = () => hoerer.forEach((f) => f(aktuell));

export function zeigeChatHinweis(h: Omit<ChatHinweis, "id">) {
  aktuell = { ...h, id: ++seq };
  melde();
}
export function schliesseChatHinweis() {
  aktuell = null;
  melde();
}

// Faden, den der Mini-Chat gerade zeigt (dort braucht es keinen Hinweis)
let miniFaden: string | null = null;
export function setMiniFaden(talentId: string | null) {
  miniFaden = talentId;
  if (aktuell && talentId && aktuell.talentId === talentId) schliesseChatHinweis();
}
/** Hat die Person diesen Faden gerade vor sich? Chatseite des Creators (Agentur), Chatseite (Creator) oder Mini-Chat. */
export function fadenOffen(portal: "agentur" | "creator", talentId: string, pfad: string): boolean {
  if (portal === "creator") return pfad.startsWith("/creator/nachrichten");
  return miniFaden === talentId || pfad.replace(/\/+$/, "") === `/agentur/nachrichten/${talentId}`;
}

export function ChatHinweise() {
  const [h, setH] = useState<ChatHinweis | null>(aktuell);
  const [halten, setHalten] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    hoerer.add(setH);
    return () => {
      hoerer.delete(setH);
    };
  }, []);
  useEffect(() => {
    if (!h || halten) return;
    const id = h.id;
    const timer = window.setTimeout(() => {
      if (aktuell?.id === id) schliesseChatHinweis();
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [h, halten]);
  if (!h) return null;
  return (
    <div className="tk-chat-hinweis tk-noprint" role="status" aria-live="polite" onMouseEnter={() => setHalten(true)} onMouseLeave={() => setHalten(false)}>
      <button type="button" className="tk-chat-hinweis-oeffnen" onClick={() => { schliesseChatHinweis(); navigate(h.ziel); }}
        aria-label={t(`Neue Nachricht von ${h.von} öffnen`, `Open new message from ${h.von}`)}>
        <span className="tk-chat-avatar" aria-hidden="true">{initials(h.von)}</span>
        <span className="tk-chat-hinweis-text">
          <strong>{t(`Neue Nachricht von ${h.von}`, `New message from ${h.von}`)}</strong>
          {h.text && <span>{h.text}</span>}
        </span>
      </button>
      <button type="button" className="tk-quiet tk-chat-hinweis-zu" onClick={schliesseChatHinweis} aria-label={t("Hinweis schließen", "Close notice")}>×</button>
    </div>
  );
}
